import { PlusIcon } from '@heroicons/react/24/outline';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Button } from '@/components/ui/daisy';
import Fieldset from '@/components/ui/daisy/fieldset';
import useAssessmentCriteriaForm from '@/data/hook/evaluation/useAssessmentCriteriaForm';
import { ActivityBlockType } from '@/models/Activity';
import { AssessmentItemTypeEnum } from '@/models/Assessment';

import compact from 'lodash/compact';
import AssessmentCriteriaItem from './AssessmentCriteriaItem';
import { AssessmentItemFormFields } from './AssessmentItemForm';

export default function AssessmentCriteriaForm() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.evaluationsTab',
  });

  const { control } = useFormContext<AssessmentItemFormFields>();
  const [parentType, criteriaValue] = useWatch({
    control,
    name: ['type', 'criteria'],
  });

  const {
    fields,
    error: criteriaError,
    handleAdd,
    handleDelete,
    getMaxAvailable,
  } = useAssessmentCriteriaForm();

  const usedActivityTypes = new Set(
    compact(criteriaValue?.map(field => field.activityType)),
  );
  const isAllActivityTypesUsed =
    parentType === AssessmentItemTypeEnum.DIGITAL_ACTIVITY &&
    usedActivityTypes.size === Object.keys(ActivityBlockType).length;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <span className="text-sm font-semibold text-base-content">
          {t('criteriaSection')}
        </span>
        <ConditionalRenderer condition={criteriaError?.root?.message}>
          <Fieldset.Label className="text-error text-xs">
            {criteriaError?.root?.message}
          </Fieldset.Label>
        </ConditionalRenderer>
      </div>

      {fields.map((field, index) => (
        <AssessmentCriteriaItem
          key={field.fieldId}
          index={index}
          criterion={field}
          maxAvailable={getMaxAvailable(index)}
          onDelete={() => handleDelete(index)}
          usedActivityTypes={usedActivityTypes}
        />
      ))}

      <Button
        type="button"
        variant="soft"
        color="primary"
        size="sm"
        className="self-start"
        onClick={handleAdd}
        disabled={isAllActivityTypesUsed}
      >
        <PlusIcon className="w-3 h-3" />
        {t('addCriteria')}
      </Button>
    </div>
  );
}
